(() => {
  'use strict';
  const pendingSubmissions = new WeakMap();
  const nativeSubmissions = new WeakMap();
  function cancelPendingSubmit(form) {
    const pending = pendingSubmissions.get(form);
    if (!pending) return;
    clearTimeout(pending.timer);
    cancelAnimationFrame(pending.frame);
    pendingSubmissions.delete(form);
  }
  function isNativeResubmission(event) {
    const native = nativeSubmissions.get(event.currentTarget);
    if (!native) return false;
    native.event = event;
    return true;
  }
  function prepareNativeSubmit(event, button, reset) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !button) return;
    // WebKit can freeze a newly started transition during page navigation.
    // Finish the brief feedback first, then retain native validation and the
    // original submitter (especially name="checkout"). No AJAX cart rewrite.
    event.preventDefault();
    const form = event.currentTarget;
    const submitter = event.submitter;
    const pending = {};
    pendingSubmissions.set(form, pending);
    const transitions = () => button.getAnimations({ subtree: true })
      .filter(animation => 'transitionProperty' in animation);
    function submit() {
      if (pendingSubmissions.get(form) !== pending) return;
      cancelPendingSubmit(form);
      if (!form.isConnected) return;
      // The timeout also covers a background tab whose rendering is paused.
      transitions().forEach(animation => animation.finish());
      const wasDisabled = button.disabled;
      button.disabled = false;
      const native = {};
      nativeSubmissions.set(form, native);
      try {
        form.requestSubmit(submitter || undefined);
      } finally {
        nativeSubmissions.delete(form);
        if (!native.event || native.event.defaultPrevented) reset();
        else button.disabled = wasDisabled;
      }
    }
    pending.timer = setTimeout(submit, 250);
    pending.frame = requestAnimationFrame(() => {
      Promise.allSettled(transitions().map(animation => animation.finished)).then(() => {
        if (pendingSubmissions.get(form) === pending) pending.frame = requestAnimationFrame(submit);
      });
    });
  }
  // The browser still submits Shopify's native forms. Only add feedback and
  // open optional fields when native validation needs to focus them.
  document.querySelectorAll('.service-product-form, .cart-layout').forEach(form => {
    form.addEventListener('invalid', event => {
      const disclosure = event.target.closest('details');
      if (disclosure) disclosure.open = true;
    }, true);
  });

  const productForm = document.getElementById('ServiceProductForm');
  if (productForm) {
    const quantity = productForm.querySelector('[name="quantity"]');
    const total = productForm.querySelector('[data-purchase-total]');
    // Shopify's money format belongs to the active market/store, not the page
    // language. A non-round Liquid sample reveals its affixes, grouping, and
    // decimal precision, including every standard Shopify money placeholder.
    function createMoneyFormatter(sample) {
      const fragment = document.createElement('template');
      fragment.innerHTML = sample;
      const text = fragment.content.textContent;
      const number = [...text.matchAll(/\d[\d.,'’\s]*\d/g)].find(match => {
        const digits = match[0].replace(/\D/g, '');
        return digits === '123456789' || digits === '1234568';
      });
      if (!number) return null;
      const amount = number[0];
      const hasDecimals = amount.replace(/\D/g, '') === '123456789';
      const decimalMark = hasDecimals ? amount.slice(-3, -2) : '';
      const integerSample = hasDecimals ? amount.slice(0, -3) : amount;
      const groups = integerSample.split(/\D+/);
      const groupMark = integerSample.match(/\D+/)?.[0] || '';
      const primarySize = groups.at(-1).length;
      const secondarySize = groups.length > 2 ? groups.at(-2).length : primarySize;
      const prefix = text.slice(0, number.index);
      const suffix = text.slice(number.index + amount.length);
      return cents => {
        if (!Number.isSafeInteger(cents) || cents < 0) return '—';
        let integer = String(hasDecimals ? Math.floor(cents / 100) : Math.round(cents / 100));
        if (groupMark) {
          const parts = [];
          let size = primarySize;
          while (integer.length > size) {
            parts.unshift(integer.slice(-size));
            integer = integer.slice(0, -size);
            size = secondarySize;
          }
          parts.unshift(integer);
          integer = parts.join(groupMark);
        }
        const fraction = hasDecimals ? decimalMark + String(cents % 100).padStart(2, '0') : '';
        return prefix + integer + fraction + suffix;
      };
    }
    const formatMoney = createMoneyFormatter(productForm.dataset.moneySample || '');
    const fallbackFormatter = formatMoney ? null : new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
    function updatePrice() {
      const variant = productForm.querySelector('[name="id"]:checked');
      if (!variant) return;
      const button = productForm.querySelector('[type="submit"]');
      if (!button.hasAttribute('aria-busy')) button.disabled = variant.disabled;
      const units = Number(quantity.value);
      if (quantity.validity.valid && Number.isInteger(units) && units > 0) {
        const cents = Number(variant.dataset.variantPrice) * units;
        total.textContent = formatMoney ? formatMoney(cents) : fallbackFormatter.format(cents / 100);
      } else {
        total.textContent = '—';
      }
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.value);
      history.replaceState(null, '', url);
    }
    productForm.addEventListener('change', event => {
      if (event.target.matches('[name="id"], [name="quantity"]')) updatePrice();
    });
    quantity.addEventListener('input', updatePrice);
    productForm.addEventListener('submit', event => {
      if (isNativeResubmission(event) || event.defaultPrevented) return;
      const button = productForm.querySelector('[type="submit"]');
      if (button.getAttribute('aria-busy') === 'true') {
        event.preventDefault();
        return;
      }
      button.setAttribute('aria-busy', 'true');
      productForm.setAttribute('aria-busy', 'true');
      button.disabled = true;
      prepareNativeSubmit(event, button, resetProductState);
    });
    function resetProductState() {
      cancelPendingSubmit(productForm);
      const button = productForm.querySelector('[type="submit"]');
      button.removeAttribute('aria-busy');
      productForm.removeAttribute('aria-busy');
      button.disabled = !productForm.querySelector('[name="id"]:checked:not(:disabled)');
      updatePrice();
    }
    window.addEventListener('pageshow', resetProductState);
    updatePrice();
  }
  const cartForm = document.getElementById('ServiceCartForm');
  if (cartForm) {
    const updateButton = cartForm.querySelector('.cart-update');
    const updateNotice = cartForm.querySelector('[data-cart-change-notice]');
    function updateCartState() {
      const changed = [...cartForm.querySelectorAll('[data-cart-quantity]')].some(input => input.value !== input.defaultValue);
      updateNotice.hidden = !changed;
      updateButton.dataset.cartDirty = String(changed);
    }
    cartForm.addEventListener('input', event => {
      if (!event.target.matches('[data-cart-quantity]')) return;
      updateCartState();
    });
    cartForm.addEventListener('submit', event => {
      if (isNativeResubmission(event) || event.defaultPrevented) return;
      if (cartForm.getAttribute('aria-busy') === 'true') {
        event.preventDefault();
        return;
      }
      cartForm.setAttribute('aria-busy', 'true');
      // Disabling the submitter would remove name="checkout" from the POST.
      event.submitter?.setAttribute('aria-busy', 'true');
      prepareNativeSubmit(event, event.submitter, resetCartState);
    });
    function resetCartState() {
      cancelPendingSubmit(cartForm);
      cartForm.removeAttribute('aria-busy');
      cartForm.querySelectorAll('[aria-busy]').forEach(button => button.removeAttribute('aria-busy'));
      updateCartState();
    }
    window.addEventListener('pageshow', resetCartState);
    updateCartState();
  }
})();
